import { useEffect, useMemo, useRef, useState } from 'react';
import {
  clearGoogleMapsInstanceListeners,
  isGoogleMapsMissingApiKeyError,
  loadGoogleMapsScript,
  type GoogleAddressComponent,
  type GoogleGeocoderResult,
  type GoogleLatLngLiteral,
  type GoogleMapInstance,
  type GoogleMapMouseEvent,
  type GoogleMarkerInstance,
  type GoogleMapsListener,
} from '~/app/lib/google-maps';

export type SiteCoordinates = {
  latitude: number;
  longitude: number;
};

export type SiteCoordinateSelection = SiteCoordinates & {
  address?: string;
  cityName?: string;
  provinceName?: string;
  postalCode?: string;
};

type UseSiteCoordinatePickerMapParams = {
  addressQuery: string | null;
  selectedCoordinates: SiteCoordinates | null;
  disabled: boolean;
  missingKeyText: string;
  geocodeFailedText: string;
  loadFailedText: string;
  onCoordinatesSelect: (coordinates: SiteCoordinates) => void;
  onLocationSelect?: (
    selection: SiteCoordinateSelection,
  ) => Promise<void> | void;
};

function toLatLngLiteral(result: GoogleGeocoderResult): GoogleLatLngLiteral {
  return {
    lat: result.geometry.location.lat(),
    lng: result.geometry.location.lng(),
  };
}

async function geocodeAddress(address: string): Promise<GoogleLatLngLiteral> {
  const googleMaps = await loadGoogleMapsScript();
  const geocoder = new googleMaps.maps.Geocoder();

  return new Promise<GoogleLatLngLiteral>((resolve, reject) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status !== 'OK' || !results?.[0]) {
        reject(new Error('GEOCODE_FAILED'));
        return;
      }

      resolve(toLatLngLiteral(results[0]));
    });
  });
}

function getAddressComponentValue(
  addressComponents: GoogleAddressComponent[] | undefined,
  componentTypes: string[],
): string | undefined {
  return addressComponents?.find((component) =>
    componentTypes.every((componentType) =>
      component.types.includes(componentType),
    ),
  )?.long_name;
}

async function reverseGeocodeCoordinates(
  coordinates: SiteCoordinates,
): Promise<SiteCoordinateSelection> {
  const googleMaps = await loadGoogleMapsScript();
  const geocoder = new googleMaps.maps.Geocoder();

  return new Promise<SiteCoordinateSelection>((resolve, reject) => {
    geocoder.geocode(
      {
        location: {
          lat: coordinates.latitude,
          lng: coordinates.longitude,
        },
      },
      (results, status) => {
        if (status !== 'OK' || !results?.[0]) {
          reject(new Error('GEOCODE_FAILED'));
          return;
        }

        const result = results[0];
        const cityName =
          getAddressComponentValue(result.address_components, ['locality']) ??
          getAddressComponentValue(result.address_components, [
            'postal_town',
          ]) ??
          getAddressComponentValue(result.address_components, [
            'administrative_area_level_3',
          ]);
        const provinceName = getAddressComponentValue(
          result.address_components,
          ['administrative_area_level_1'],
        );
        const postalCode =
          result.address_components?.find((component) =>
            component.types.includes('postal_code'),
          )?.short_name ?? result.formatted_address?.match(/\b\d{5}\b/)?.[0];

        resolve({
          ...coordinates,
          address: result.formatted_address,
          cityName,
          provinceName,
          postalCode,
        });
      },
    );
  });
}

export function useSiteCoordinatePickerMap({
  addressQuery,
  selectedCoordinates,
  disabled,
  missingKeyText,
  geocodeFailedText,
  loadFailedText,
  onCoordinatesSelect,
  onLocationSelect,
}: UseSiteCoordinatePickerMapParams) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<GoogleMapInstance | null>(null);
  const markerRef = useRef<GoogleMarkerInstance | null>(null);
  const clickListenerRef = useRef<GoogleMapsListener | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLocating, setIsLocating] = useState(false);

  const normalizedQuery = useMemo(
    () => addressQuery?.trim() ?? '',
    [addressQuery],
  );

  useEffect(() => {
    if (disabled || !mapContainerRef.current) {
      return;
    }

    let isCancelled = false;

    setIsLocating(true);

    void loadGoogleMapsScript()
      .then(async (googleMaps) => {
        if (isCancelled || !mapContainerRef.current) {
          return;
        }

        setErrorMessage(null);

        if (!mapRef.current) {
          mapRef.current = new googleMaps.maps.Map(mapContainerRef.current, {
            center: { lat: 46.2276, lng: 2.2137 },
            zoom: 5,
            disableDefaultUI: false,
            fullscreenControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            zoomControl: true,
          });
        }

        clickListenerRef.current?.remove();
        clickListenerRef.current = mapRef.current.addListener(
          'click',
          (event: GoogleMapMouseEvent) => {
            const clickedLatLng = event.latLng;

            if (!clickedLatLng || !mapRef.current) {
              return;
            }

            const nextCoordinates = {
              latitude: clickedLatLng.lat(),
              longitude: clickedLatLng.lng(),
            };

            if (!markerRef.current) {
              markerRef.current = new googleMaps.maps.Marker({
                map: mapRef.current,
                position: {
                  lat: nextCoordinates.latitude,
                  lng: nextCoordinates.longitude,
                },
                draggable: false,
              });
            } else {
              markerRef.current.setPosition({
                lat: nextCoordinates.latitude,
                lng: nextCoordinates.longitude,
              });
            }

            onCoordinatesSelect(nextCoordinates);

            if (onLocationSelect) {
              void reverseGeocodeCoordinates(nextCoordinates)
                .then((selection) => onLocationSelect(selection))
                .catch(() => {
                  setErrorMessage(geocodeFailedText);
                });
            }
          },
        );

        if (selectedCoordinates) {
          const center = {
            lat: selectedCoordinates.latitude,
            lng: selectedCoordinates.longitude,
          };

          mapRef.current.setCenter(center);
          mapRef.current.setZoom(15);

          if (!markerRef.current) {
            markerRef.current = new googleMaps.maps.Marker({
              map: mapRef.current,
              position: center,
              draggable: false,
            });
          } else {
            markerRef.current.setPosition(center);
          }

          setIsLocating(false);
          return;
        }

        if (!normalizedQuery) {
          setIsLocating(false);
          return;
        }

        try {
          const geocodedCenter = await geocodeAddress(normalizedQuery);

          if (isCancelled || !mapRef.current) {
            return;
          }

          mapRef.current.setCenter(geocodedCenter);
          mapRef.current.setZoom(12);

          if (markerRef.current) {
            markerRef.current.setMap(null);
            markerRef.current = null;
          }
        } catch (error) {
          if (isCancelled) {
            return;
          }

          setErrorMessage(
            error instanceof Error && error.message === 'GEOCODE_FAILED'
              ? geocodeFailedText
              : loadFailedText,
          );
        } finally {
          if (!isCancelled) {
            setIsLocating(false);
          }
        }
      })
      .catch((error: Error) => {
        if (isCancelled) {
          return;
        }

        setErrorMessage(
          isGoogleMapsMissingApiKeyError(error)
            ? missingKeyText
            : loadFailedText,
        );
        setIsLocating(false);
      });

    return () => {
      isCancelled = true;
      clickListenerRef.current?.remove();
      clearGoogleMapsInstanceListeners(markerRef.current);
      clearGoogleMapsInstanceListeners(mapRef.current);
    };
  }, [
    disabled,
    geocodeFailedText,
    loadFailedText,
    missingKeyText,
    normalizedQuery,
    onLocationSelect,
    onCoordinatesSelect,
    selectedCoordinates,
  ]);

  return {
    errorMessage,
    isLocating,
    mapContainerRef,
  };
}
